import { useState, useEffect, useCallback } from 'react';
import API_CONFIG from '../../config/api';
import './AdminModals.css';

function ViewUserModal({ isOpen, onClose, user, adminData }) {
    const [userDetails, setUserDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const [documents, setDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [digioForm, setDigioForm] = useState({
        customer_identifier: '',
        customer_name: '',
        template_name: 'SELFIE_KYC',
        notify_customer: true,
        customer_notification_mode: 'SMS',
        expire_in_days: 10,
        generate_access_token: false,
        generate_deeplink_info: true
    });
    const [digioCreating, setDigioCreating] = useState(false);
    const [digioMessage, setDigioMessage] = useState('');
    const [vehicleRcForm, setVehicleRcForm] = useState({
        id: '',
        unique_request_id: ''
    });
    const [vehicleRcLoading, setVehicleRcLoading] = useState(false);
    const [vehicleRcMessage, setVehicleRcMessage] = useState('');

    const fetchUserDetails = useCallback(async () => {
        if (!user?.id) return;
        // Fallback for adminId if not passed correctly via prop
        const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;

        setIsLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}`, {
                headers: { 'x-user-id': adminId }
            });
            const data = await res.json();
            if (data.success) {
                setUserDetails(data.data.user);
            } else {
                alert('Failed to fetch user details');
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
            alert('Error fetching user details');
        } finally {
            setIsLoading(false);
        }
    }, [user?.id, adminData?.userId]);

    const fetchUserDocuments = useCallback(async () => {
        if (!user?.id) return;
        const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
        setDocsLoading(true);
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}/documents`, {
                headers: { 'x-user-id': adminId }
            });
            const data = await res.json();
            if (data.success) {
                setDocuments(data.data);
            }
        } catch (error) {
            console.error('Error fetching user documents:', error);
        } finally {
            setDocsLoading(false);
        }
    }, [user?.id, adminData?.userId]);

    useEffect(() => {
        if (isOpen && user) {
            fetchUserDetails();
            fetchUserDocuments();
        }
    }, [isOpen, user, fetchUserDetails, fetchUserDocuments]);

    useEffect(() => {
        if (!userDetails) return;
        const fullName = `${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim();
        setDigioForm(prev => ({
            ...prev,
            customer_identifier: userDetails.email || userDetails.phone || prev.customer_identifier,
            customer_name: fullName || userDetails.email || userDetails.phone || prev.customer_name
        }));
        setVehicleRcForm(prev => ({
            ...prev,
            id: prev.id || ''
        }));
    }, [userDetails]);

    const canUploadAgreement = adminData?.role === 'super_admin' || adminData?.permissions?.includes('users.agreement_upload');

    const handleAgreementUpload = async (file) => {
        if (!file || !canUploadAgreement) return;
        setUploadError('');
        setUploading(true);
        try {
            const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
            const base64 = await fileToBase64(file);
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/users/${user.id}/agreement`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({
                    fileName: file.name,
                    fileData: base64
                })
            });
            const data = await res.json();
            if (!data.success) {
                setUploadError(data.message || 'Failed to upload agreement');
            } else {
                await fetchUserDetails();
            }
        } catch (err) {
            console.error('Agreement upload error:', err);
            setUploadError('Network error while uploading');
        } finally {
            setUploading(false);
        }
    };

    const fileToBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const handleCreateDigioRequest = async () => {
        if (!digioForm.customer_identifier || !digioForm.template_name) {
            setDigioMessage('Customer identifier and template name are required.');
            return;
        }
        const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
        setDigioCreating(true);
        setDigioMessage('');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/kyc/digio/request`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({
                    userId: userDetails.id,
                    customer_identifier: digioForm.customer_identifier,
                    customer_name: digioForm.customer_name,
                    template_name: digioForm.template_name,
                    notify_customer: digioForm.notify_customer,
                    customer_notification_mode: digioForm.customer_notification_mode,
                    expire_in_days: digioForm.expire_in_days,
                    generate_access_token: digioForm.generate_access_token,
                    generate_deeplink_info: digioForm.generate_deeplink_info
                })
            });
            const data = await res.json();
            if (data.success) {
                setDigioMessage('Digio request created successfully.');
                await fetchUserDetails();
            } else {
                setDigioMessage(data.message || 'Failed to create Digio request');
            }
        } catch (err) {
            console.error('Digio request error:', err);
            setDigioMessage('Network error while creating Digio request');
        } finally {
            setDigioCreating(false);
        }
    };

    const handleVerifyVehicleRc = async () => {
        if (!vehicleRcForm.id) {
            setVehicleRcMessage('Vehicle RC number is required.');
            return;
        }
        const adminId = adminData?.userId || JSON.parse(localStorage.getItem('adminSession'))?.userId;
        setVehicleRcLoading(true);
        setVehicleRcMessage('');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/kyc/digio/vehicle-rc`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-user-id': adminId
                },
                body: JSON.stringify({
                    userId: userDetails.id,
                    id: vehicleRcForm.id,
                    unique_request_id: vehicleRcForm.unique_request_id || undefined
                })
            });
            const data = await res.json();
            if (data.success) {
                setVehicleRcMessage('Vehicle RC verified successfully.');
                await fetchUserDetails();
            } else {
                setVehicleRcMessage(data.message || 'Failed to verify vehicle RC');
            }
        } catch (err) {
            console.error('Vehicle RC verify error:', err);
            setVehicleRcMessage('Network error while verifying vehicle RC');
        } finally {
            setVehicleRcLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content view-user-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>👤 User Profile</h2>
                    <button className="modal-close-btn" onClick={onClose}>×</button>
                </div>

                {isLoading ? (
                    <div className="modal-body">
                        <p className="loading-text">Loading user details...</p>
                    </div>
                ) : userDetails ? (
                    <div className="modal-body">
                        <div className="user-view-shell">
                            <div className="user-hero">
                                <div className="identity-block">
                                    <div className="avatar-ring">{(userDetails.first_name || userDetails.email || 'U').charAt(0).toUpperCase()}</div>
                                    <div>
                                        <h3>{`${userDetails.first_name || ''} ${userDetails.last_name || ''}`.trim() || 'Unnamed User'}</h3>
                                        <p>{userDetails.email}</p>
                                        <p className="muted">{userDetails.phone || 'No phone on file'}</p>
                                    </div>
                                </div>
                                <div className="cibil-score-badge">
                                    <span className="cibil-label">CIBIL Score</span>
                                    <div className="cibil-value" style={{
                                        color: (userDetails.credit_score || 0) >= 750 ? '#16a34a' :
                                            (userDetails.credit_score || 0) >= 650 ? '#d97706' : '#dc2626'
                                    }}>
                                        {userDetails.credit_score || 'N/A'}
                                    </div>
                                </div>
                                <div className="chip-row">
                                    <span className={`chip ${userDetails.is_active ? 'chip-success' : 'chip-danger'}`}>
                                        {userDetails.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                    <span className={`chip ${userDetails.kyc_status === 'completed' ? 'chip-success' :
                                        (userDetails.kyc_status === 'pending' || userDetails.kyc_status === 'in_progress') ? 'chip-warning' : 'chip-danger'
                                        }`}>
                                        KYC: {userDetails.kyc_status || 'Not started'}
                                    </span>
                                    <span className="chip chip-info">ID #{userDetails.id}</span>
                                </div>
                            </div>

                            <div className="kpi-row">
                                <div className="kpi-card">
                                    <span>Available Credit</span>
                                    <strong>₹{(userDetails.available_credit || 0).toLocaleString('en-IN')}</strong>
                                </div>
                                <div className="kpi-card">
                                    <span>Total Loans</span>
                                    <strong>{userDetails.total_loans || 0}</strong>
                                </div>
                            </div>

                            <div className="panel-grid">
                                <div className="panel-card">
                                    <h4>Account Status</h4>
                                    <div className="detail-list">
                                        <div className="detail-row"><span>Status</span><span className={`status-badge ${userDetails.is_active ? 'status-success' : 'status-danger'}`}>{userDetails.is_active ? 'Active' : 'Inactive'}</span></div>
                                        <div className="detail-row"><span>Email Verified</span><strong>{userDetails.email_verified ? 'Yes' : 'No'}</strong></div>
                                        <div className="detail-row"><span>KYC Level</span><strong>{userDetails.kyc_status || 'Pending'}</strong></div>
                                    </div>
                                </div>

                                <div className="panel-card">
                                    <h4>Timeline</h4>
                                    <div className="detail-list">
                                        <div className="detail-row"><span>Joined</span><strong>{new Date(userDetails.created_at).toLocaleDateString()}</strong></div>
                                        <div className="detail-row"><span>Updated</span><strong>{new Date(userDetails.updated_at).toLocaleDateString()}</strong></div>
                                        <div className="detail-row"><span>Last Login</span><strong>{userDetails.last_login_at ? new Date(userDetails.last_login_at).toLocaleString() : 'Never'}</strong></div>
                                    </div>
                                </div>

                                <div className="panel-card">
                                    <h4>Contact Info</h4>
                                    <div className="detail-list">
                                        <div className="detail-row"><span>Phone</span><strong>{userDetails.phone || '—'}</strong></div>
                                        <div className="detail-row"><span>Email</span><strong>{userDetails.email}</strong></div>
                                        <div className="detail-row"><span>Location</span><strong>{userDetails.city || '—'}</strong></div>
                                    </div>
                                </div>

                                <div className="panel-card digio-panel">
                                    <h4>🧾 DigiLocker KYC</h4>
                                    <div className="detail-list">
                                        <div className="detail-row"><span>Digio KID</span><strong>{userDetails.digio_kid || '—'}</strong></div>
                                        <div className="detail-row"><span>Status</span><strong>{userDetails.kyc_status || 'pending'}</strong></div>
                                    </div>
                                    <div className="digio-form">
                                        <div className="digio-field">
                                            <label>Customer Identifier (Email/Phone)</label>
                                            <input
                                                type="text"
                                                value={digioForm.customer_identifier}
                                                onChange={(e) => setDigioForm({ ...digioForm, customer_identifier: e.target.value })}
                                                placeholder="someone@gmail.com or 9999999999"
                                            />
                                        </div>
                                        <div className="digio-field">
                                            <label>Customer Name</label>
                                            <input
                                                type="text"
                                                value={digioForm.customer_name}
                                                onChange={(e) => setDigioForm({ ...digioForm, customer_name: e.target.value })}
                                                placeholder="Customer name"
                                            />
                                        </div>
                                        <div className="digio-grid">
                                            <div className="digio-field">
                                                <label>Template Name</label>
                                                <input
                                                    type="text"
                                                    value={digioForm.template_name}
                                                    onChange={(e) => setDigioForm({ ...digioForm, template_name: e.target.value })}
                                                    placeholder="FACEMATCH-VERIFY"
                                                />
                                            </div>
                                            <div className="digio-field">
                                                <label>Notify Customer</label>
                                                <select
                                                    value={digioForm.notify_customer ? 'true' : 'false'}
                                                    onChange={(e) => setDigioForm({ ...digioForm, notify_customer: e.target.value === 'true' })}
                                                >
                                                    <option value="true">Yes</option>
                                                    <option value="false">No</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="digio-grid">
                                            <div className="digio-field">
                                                <label>Notification Mode</label>
                                                <select
                                                    value={digioForm.customer_notification_mode}
                                                    onChange={(e) => setDigioForm({ ...digioForm, customer_notification_mode: e.target.value })}
                                                >
                                                    <option value="SMS">SMS</option>
                                                    <option value="WHATSAPP">WhatsApp</option>
                                                    <option value="ALL">All</option>
                                                </select>
                                            </div>
                                            <div className="digio-field">
                                                <label>Expire In (Days)</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="90"
                                                    value={digioForm.expire_in_days}
                                                    onChange={(e) => setDigioForm({ ...digioForm, expire_in_days: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="digio-actions">
                                            <button className="btn-primary" onClick={handleCreateDigioRequest} disabled={digioCreating}>
                                                {digioCreating ? 'Creating...' : 'Create Digio Request'}
                                            </button>
                                        </div>
                                        {digioMessage && <p className="digio-message">{digioMessage}</p>}
                                    </div>
                                </div>

                                <div className="panel-card digio-panel">
                                    <h4>🚗 Vehicle RC Verification</h4>
                                    <div className="digio-form">
                                        <div className="digio-field">
                                            <label>Vehicle RC Number</label>
                                            <input
                                                type="text"
                                                value={vehicleRcForm.id}
                                                onChange={(e) => setVehicleRcForm({ ...vehicleRcForm, id: e.target.value })}
                                                placeholder="e.g. MH12AB1234"
                                            />
                                        </div>
                                        <div className="digio-field">
                                            <label>Unique Request ID (optional)</label>
                                            <input
                                                type="text"
                                                value={vehicleRcForm.unique_request_id}
                                                onChange={(e) => setVehicleRcForm({ ...vehicleRcForm, unique_request_id: e.target.value })}
                                                placeholder="Auto-generated if empty"
                                            />
                                        </div>
                                        <div className="digio-actions">
                                            <button className="btn-primary" onClick={handleVerifyVehicleRc} disabled={vehicleRcLoading}>
                                                {vehicleRcLoading ? 'Verifying...' : 'Verify RC'}
                                            </button>
                                        </div>
                                        {vehicleRcMessage && <p className="digio-message">{vehicleRcMessage}</p>}
                                        {userDetails.kyc_data && (
                                            (() => {
                                                let parsed = null;
                                                try { parsed = typeof userDetails.kyc_data === 'string' ? JSON.parse(userDetails.kyc_data) : userDetails.kyc_data; } catch (e) { }
                                                const rc = parsed?.vehicle_rc;
                                                if (!rc) return null;
                                                return (
                                                    <div className="digio-field">
                                                        <label>Last RC Response</label>
                                                        <pre style={{ maxHeight: '160px', overflow: 'auto', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                            {JSON.stringify(rc.response || rc, null, 2)}
                                                        </pre>
                                                    </div>
                                                );
                                            })()
                                        )}
                                    </div>
                                </div>

                                <div className="panel-card doc-grid-section">
                                    <h4>📂 User Documents & Agreements</h4>
                                    <div className="doc-grid">
                                        {/* Agreement Card */}
                                        <div className="doc-card" onClick={() => {
                                            if (userDetails.agreement_signed_url || userDetails.agreement_file_path) {
                                                window.open(userDetails.agreement_signed_url || (userDetails.agreement_file_path.startsWith('http') ? userDetails.agreement_file_path : `${API_CONFIG.BASE_URL}${userDetails.agreement_file_path}`), '_blank');
                                            }
                                        }}>
                                            <div className="doc-icon">📜</div>
                                            <div className="doc-info">
                                                <span className="doc-name">User Agreement</span>
                                                <span className="doc-meta">
                                                    {userDetails.agreement_uploaded_at ? new Date(userDetails.agreement_uploaded_at).toLocaleDateString() : 'Not Signed'}
                                                </span>
                                            </div>
                                            {(userDetails.agreement_signed_url || userDetails.agreement_file_path) && (
                                                <div className="view-btn-overlay">
                                                    <button className="btn-view-doc">View Agreement</button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Dynamic Documents */}
                                        {docsLoading ? (
                                            <div className="doc-empty-state">Loading documents...</div>
                                        ) : documents.map((doc, idx) => (
                                            <div key={idx} className="doc-card" onClick={() => window.open(doc.url, '_blank')}>
                                                <div className="doc-icon">
                                                    {doc.name.endsWith('.pdf') ? '📄' : '🖼️'}
                                                </div>
                                                <div className="doc-info">
                                                    <span className="doc-name" title={doc.name}>{doc.name}</span>
                                                    <span className="doc-meta">{(doc.size / 1024).toFixed(0)} KB • {new Date(doc.lastModified).toLocaleDateString()}</span>
                                                </div>
                                                <div className="view-btn-overlay">
                                                    <button className="btn-view-doc">View Document</button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Upload Action */}
                                        {canUploadAgreement && (
                                            <div className="upload-area" onClick={() => document.getElementById('agreement-upload-input').click()}>
                                                <div className="upload-icon">☁️</div>
                                                <p>{uploading ? 'Uploading...' : 'Upload Agreement'}</p>
                                                <input
                                                    id="agreement-upload-input"
                                                    type="file"
                                                    hidden
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => handleAgreementUpload(e.target.files[0])}
                                                    disabled={uploading}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {uploadError && <p className="error-text mt-2">{uploadError}</p>}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="modal-body">
                        <p>No user details available</p>
                    </div>
                )}

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default ViewUserModal;
